import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { styled } from '@mui/material/styles';
import { Link, Stack, Typography, TextField, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotificationsProvider } from '../../context/NotificationsContext';
// component
import Iconify from '../../components/Iconify';
import Logo from '../../components/Logo';
import translateCognitoError from '../../utils/translateCognitoError';

import { login, verify2FA, completeNewPassword, sendCognitoMFACode } from './actions'
import {
  selectSession,
  selectUserError,
  selectTwoFactorRequired,
  selectTwoFactorData,
  selectNewPasswordRequired,
  selectNewPasswordChallenge,
  selectCognitoTotpRequired
} from './selectors';

// ----------------------------------------------------------------------
const LoginStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

const ContainerStyle = styled(Box)(({ theme }) => ({
  shadowColor: theme.palette.secondary,
  backgroundColor: theme.palette.grey,
  shadowOpacity: 0.8,
  shadowRadius: 2,
  shadowOffset: {
    height: 1,
    width: 1
  },
  elevation: 5,
}));

const LogoStyle = {
  width: 175,
  height: 48
};

export default function LoginForm() {
  const { t } = useTranslation()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showNotification } = useNotificationsProvider();

  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email(t('login.invalid-email')).required(t('login.required-email')),
    password: Yup.string().required(t('login.required-password')),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: (data) => {
      dispatch(login(data.email, data.password))
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setSubmitting } = formik;

  const isAuth = useSelector(selectSession());
  const userError = useSelector(selectUserError());
  const requires2FA = useSelector(selectTwoFactorRequired());
  const twoFactorData = useSelector(selectTwoFactorData());
  const requiresNewPassword = useSelector(selectNewPasswordRequired());
  const newPasswordChallenge = useSelector(selectNewPasswordChallenge());
  const requiresCognitoTotp = useSelector(selectCognitoTotpRequired());

  useEffect(() => {
    if (isAuth) {
      navigate('/dashboard/app', { replace: true });
    } else if (userError && !requires2FA && !requiresNewPassword && !requiresCognitoTotp) {
      setSubmitting(false);
      showNotification('error', translateCognitoError(userError?.error, t, 'login.invalid-credentials'));
    } else if (userError && requires2FA) {
      setSubmitting(false);
      showNotification('error', t('login.invalid-2fa-token'));
    } else if (userError && requiresNewPassword) {
      setSubmitting(false);
      showNotification('error', translateCognitoError(userError?.error, t, 'login.invalid-new-password'));
    } else if (userError && requiresCognitoTotp) {
      setSubmitting(false);
      showNotification('error', translateCognitoError(userError?.error, t, 'login.invalid-2fa-token'));
    } else if ((requires2FA || requiresNewPassword || requiresCognitoTotp) && !userError) {
      // Successfully transitioned to challenge screen - reset submitting state
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, userError, requires2FA, requiresNewPassword, requiresCognitoTotp, setSubmitting, navigate]);

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handle2FASubmit = (e) => {
    e.preventDefault();
    if (twoFactorToken.length >= 6 && twoFactorData.accountId) {
      setSubmitting(true);
      dispatch(verify2FA(dispatch, twoFactorData.accountId, twoFactorToken));
    }
  };

  const handleCognitoTotpSubmit = (e) => {
    e.preventDefault();
    if (twoFactorToken.length === 6) {
      setSubmitting(true);
      dispatch(sendCognitoMFACode(twoFactorToken));
    }
  };

  const handleNewPasswordSubmit = (e) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) {
      showNotification('error', t('login.password-mismatch', 'Passwords do not match'));
      return;
    }
    // Length / complexity rules live in the Cognito User Pool policy. Let
    // the SDK surface those failures rather than duplicating the rule here
    // where it can drift from the server-side policy.
    setSubmitting(true);
    dispatch(completeNewPassword(newPassword, newPasswordChallenge?.userAttributes || {}));
  };

  // Show "set new password" challenge form (Cognito FORCE_CHANGE_PASSWORD)
  if (requiresNewPassword) {
    return (
      <LoginStyle>
        <ContainerStyle>
          <form autoComplete="off" noValidate onSubmit={handleNewPasswordSubmit}>
            <Stack spacing={10}>
              <Stack spacing={3}>
                <Stack alignItems="center">
                  <Logo sx={LogoStyle} />
                </Stack>
                <Typography variant="h4">
                  {t('login.set-new-password-title', 'Set a new password')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('login.set-new-password-description', 'Your account requires a new password before you can sign in.')}
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  label={t('login.new-password-label', 'New password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword((s) => !s)} edge="end">
                          <Iconify icon={showNewPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  label={t('login.confirm-new-password-label', 'Confirm new password')}
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                />
              </Stack>
              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={!newPassword || newPassword !== newPasswordConfirm}
                >
                  {t('login.set-password-button', 'Set password and continue')}
                </LoadingButton>
                <Link
                  component={RouterLink}
                  variant="subtitle2"
                  to="/login"
                  underline="hover"
                  onClick={() => window.location.reload()}
                >
                  {t('login.back-to-login', 'Back to Login')}
                </Link>
              </Stack>
            </Stack>
          </form>
        </ContainerStyle>
      </LoginStyle>
    );
  }

  // Show Cognito TOTP MFA challenge form (Software Token MFA)
  if (requiresCognitoTotp) {
    return (
      <LoginStyle>
        <ContainerStyle>
          <form autoComplete="off" noValidate onSubmit={handleCognitoTotpSubmit}>
            <Stack spacing={10}>
              <Stack spacing={3}>
                <Stack alignItems="center">
                  <Logo sx={LogoStyle} />
                </Stack>
                <Typography variant="h4">
                  {t('login.2fa-title', 'Two-Factor Authentication')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('login.2fa-description-cognito', 'Enter the 6-digit code from your authenticator app.')}
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="one-time-code"
                  type="text"
                  label={t('login.2fa-code-label', 'Authentication Code')}
                  value={twoFactorToken}
                  onChange={(e) => setTwoFactorToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{
                    maxLength: 6,
                    inputMode: 'numeric',
                    style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                  }}
                />
              </Stack>
              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={twoFactorToken.length !== 6}
                >
                  {t('login.verify-button', 'Verify')}
                </LoadingButton>
                <Link
                  component={RouterLink}
                  variant="subtitle2"
                  to="/login"
                  underline="hover"
                  onClick={() => window.location.reload()}
                >
                  {t('login.back-to-login', 'Back to Login')}
                </Link>
              </Stack>
            </Stack>
          </form>
        </ContainerStyle>
      </LoginStyle>
    );
  }

  // Show 2FA verification form if required
  if (requires2FA) {
    return (
      <LoginStyle>
        <ContainerStyle>
          <form autoComplete="off" noValidate onSubmit={handle2FASubmit}>
            <Stack spacing={10}>
              <Stack spacing={3}>
                <Stack alignItems="center">
                  <Logo sx={LogoStyle} />
                </Stack>
                <Typography variant="h4">
                  {t('login.2fa-title', 'Two-Factor Authentication')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('login.2fa-description', 'Enter the 6-digit code from your authenticator app or an 8-character recovery code.')}
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="one-time-code"
                  type="text"
                  label={t('login.2fa-code-label', 'Authentication Code')}
                  value={twoFactorToken}
                  onChange={(e) => setTwoFactorToken(e.target.value)}
                  inputProps={{
                    maxLength: 8,
                    style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                  }}
                />
              </Stack>
              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={twoFactorToken.length < 6}
                >
                  {t('login.verify-button', 'Verify')}
                </LoadingButton>
                <Link
                  component={RouterLink}
                  variant="subtitle2"
                  to="/reset-2fa"
                  underline="hover"
                >
                  {t('login.lost-2fa-access', 'Lost access to authenticator?')}
                </Link>
                <Link
                  component={RouterLink}
                  variant="subtitle2"
                  to="/login"
                  underline="hover"
                  onClick={() => window.location.reload()}
                >
                  {t('login.back-to-login', 'Back to Login')}
                </Link>
              </Stack>
            </Stack>
          </form>
        </ContainerStyle>
      </LoginStyle>
    );
  }

  // Show normal login form
  return (
    <LoginStyle>
      <ContainerStyle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={10}>
              <Stack spacing={3}>
                <Stack alignItems="center">
                  <Logo sx={LogoStyle} />
                </Stack>
                <Typography variant="h4">
                  {t('login.title')}
                </Typography>
                <TextField
                  fullWidth
                  autoComplete="username"
                  type="email"
                  label={t('login.email-label')}
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />

                <TextField
                  fullWidth
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  label={t('login.password-label')}
                  {...getFieldProps('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                />
              </Stack>
              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
                  {t('login.login-button')}
                </LoadingButton>
                <Link component={RouterLink} variant="subtitle2" to="/forgot-password" underline="hover">
                  {t('login.forgot-password')}
                </Link>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </ContainerStyle>
    </LoginStyle>
  );
};